import { useMemo } from 'react';
import { useLocation, useMatch, useNavigate } from 'react-router-dom';
import { Sidebar, type SidebarGroup, type IconName } from '@beyond-js/ui/react';
import { usePresence } from '@accounts/session';
import { moved } from './screen';

/** The roles that administer an organization: its settings and activity are theirs */
const stewards = ['owner', 'admin'];
/** Icons identify the same permitted destinations in either desktop presentation. */
const glyphs: Record<string, IconName> = { 'nav.overview': 'home', 'nav.organizations': 'building', 'team.title': 'people', 'settings.title': 'settings', 'audit.title': 'clock', 'nav.account': 'user', 'nav.security': 'shield', 'nav.activity': 'clock', 'nav.tokens': 'key', 'nav.connections': 'plug' };
/** The organization's own destinations in the sidebar, by the `?view=` that opens them */
const destinations = ['members', 'settings', 'audit'] as const;
/** The views of an organization's page each destination covers: the team holds members and invitations */
const covers: Record<string, string[]> = { members: ['members', 'invitations'], settings: ['settings'], audit: ['audit'] };

/**
 * This interface's own sections on the family's shared `Sidebar` (D53): permanent from the family's
 * cut of 1024 px, a product row naming the section in view and a modal drawer below it.
 *
 * The overview and the organizations come first; then, on an organization's own screens only, that
 * organization, named as text in its group's heading, with its team (members and invitations)
 * and, for its owners and administrators, its settings and activity; then the person's own account,
 * sign-in, activity, tokens and AI apps with access. The organizations collection and the person's own pages
 * name no organization, so a list of several is never read as scoped to one; the session keeps the
 * organization it works in, which this sidebar never changes.
 *
 * The inbox is reached from the family bar's bell, whose panel ends in the full inbox, and from the
 * command palette; the sidebar does not repeat it. Choosing a destination is followed in the
 * application and moves focus to the new screen's heading, or to the organization region a link asked
 * for; a destination in another product is left to the browser.
 */
export function Sections() {
	const { text, identity, language } = usePresence();
	const location = useLocation();
	const navigate = useNavigate();
	const shown = useMatch('/organizations/:organization')?.params.organization;
	const membership = identity?.organizations.find(entry => entry.organization === shown);

	const groups = useMemo<SidebarGroup[]>(() => {
		const path = location.pathname;
		const requested = new URLSearchParams(location.search).get('view') ?? 'members';
		const view = Object.values(covers).flat().includes(requested) ? requested : 'members';
		const item = (href: string, key: string, current: boolean) => ({ label: text(key), href, current, glyph: glyphs[key] });

		const inside: SidebarGroup | null = membership
			? {
					heading: membership.name ?? membership.organization,
					items: destinations
						.filter(name => name === 'members' || stewards.includes(membership.role))
						.map(name => {
							const own = `/organizations/${encodeURIComponent(membership.organization)}?view=${name}`;
							return item(own, name === 'members' ? 'team.title' : `${name}.title`, covers[name].includes(view));
						})
				}
			: null;
		const top: SidebarGroup = {
			items: [item('/', 'nav.overview', path === '/'), item('/organizations', 'nav.organizations', path === '/organizations')]
		};
		const own: SidebarGroup = {
			heading: text('nav.group.account'),
			items: [
				item('/account', 'nav.account', path === '/account'),
				item('/security', 'nav.security', path === '/security'),
				item('/activity', 'nav.activity', path === '/activity'),
				item('/tokens', 'nav.tokens', path.startsWith('/tokens')),
				item('/connections', 'nav.connections', path === '/connections')
			]
		};
		return inside ? [top, inside, own] : [top, own];
		// The language decides every label
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [location.pathname, location.search, membership, language]);

	return (
		<Sidebar
			// One bar on a phone (DQ-19): the family bar's menu button opens the drawer, and on a narrow
			// touch screen the organizations fold into the bar's product menu (ui 0.16.2)
			merge
			product="Accounts"
			navigation={{ key: 'accounts' }}
			groups={groups}
			// The family's own copy in the interface language (0.7.2): one frozen set per language, so the
			// sidebar is rebuilt only when the language changes
			labels={Sidebar.labels[language]}
			onNavigate={target => {
				const url = new URL(target.url);
				navigate(`${url.pathname}${url.search}`, { state: moved });
			}}
		/>
	);
}
